import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { toast } from "sonner";
import {
  ArrowRight,
  ArrowUpRight,
  WhatsappLogo,
} from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";

const heroImg =
  "https://images.pexels.com/photos/3231358/pexels-photo-3231358.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200";

const tailorImg =
  "https://images.unsplash.com/photo-1606501126768-b78d4569d3f9?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000";

const lifestyle =
  "https://images.pexels.com/photos/14545192/pexels-photo-14545192.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=940";

const HERO_LINES = [
  "Engineered for",
  "the playground.",
  "Tailored for",
  "the classroom.",
];

const lineVariant = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: {
      duration: 0.9,
      delay: 0.3 + i * 0.12,
      ease: [0.16, 1, 0.3, 1],
    },
  }),
};

const chapters = [
  {
    no: "01",
    title: "The Fabric",
    body: "Breathable, colour-fast cotton blends chosen to survive recess, monsoons and a hundred wash cycles without fading.",
  },
  {
    no: "02",
    title: "The Stitch",
    body: "Double-needle seams and reinforced buttons. Every garment is stress-tested at the knees, cuffs and collar.",
  },
  {
    no: "03",
    title: "The Fit",
    body: "Graded sizing from the smallest pre-primary to senior secondary — cut for movement, comfort and a sharp silhouette.",
  },
];

export default function Home() {
  const [schools, setSchools] = useState([]);
  const [hovered, setHovered] = useState(null);

  const heroRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });

  const imgY = useTransform(
    scrollYProgress,
    [0, 1],
    ["0%", "22%"]
  );

  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    message: "",
  });

  /*
   * FIX:
   * The API may return either:
   *   [...]
   * OR
   *   { schools: [...] }
   *
   * We make sure schools is ALWAYS an array.
   */
  useEffect(() => {
    api
      .get("/schools")
      .then((r) => {
        const data = r.data;

        const schoolList = Array.isArray(data)
          ? data
          : Array.isArray(data?.schools)
          ? data.schools
          : [];

        setSchools(schoolList);
      })
      .catch((err) => {
        console.error("Failed to load schools:", err);
        setSchools([]);
      });
  }, []);

  const submitEnquiry = async (e) => {
    e.preventDefault();

    if (!form.name || !form.phone) {
      return toast.error("Name and phone are required");
    }

    try {
      await api.post("/enquiries", form);

      toast.success("Enquiry sent! We'll reach out shortly.");

      setForm({
        name: "",
        phone: "",
        email: "",
        message: "",
      });
    } catch (err) {
      toast.error(
        formatApiError(err.response?.data?.detail)
      );
    }
  };

  return (
    <div>
      {/* HERO */}
      <section
        ref={heroRef}
        className="relative min-h-screen flex items-end overflow-hidden pt-28 pb-16"
      >
        <motion.div
          className="absolute inset-0 -z-10"
          style={{ y: imgY }}
        >
          <img
            src={heroImg}
            alt="Students in uniform"
            className="w-full h-[120%] object-cover"
          />

          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-background/20" />
        </motion.div>

        <div className="max-w-[1400px] mx-auto px-6 md:px-12 w-full">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-6"
            data-testid="hero-overline"
          >
            Uniform Co. · Boutique School Atelier · Pune
          </motion.p>

          <h1 className="text-5xl md:text-7xl lg:text-[7vw] leading-[0.92] tracking-tighter font-medium max-w-5xl">
            {HERO_LINES.map((line, i) => (
              <span key={i} className="line-mask">
                <motion.span
                  custom={i}
                  variants={lineVariant}
                  initial="hidden"
                  animate="visible"
                  className={`block ${
                    i % 2 === 1 ? "italic text-accent" : ""
                  }`}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              delay: 1.1,
              duration: 0.7,
            }}
            className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-5"
          >
            <Link
              to="/shop"
              data-testid="hero-shop-button"
              className="group rounded-full bg-accent text-accent-foreground px-8 py-4 text-sm uppercase tracking-widest flex items-center gap-3 hover:bg-foreground transition-colors duration-300"
            >
              Shop Uniforms

              <ArrowRight
                size={18}
                className="group-hover:translate-x-1 transition-transform"
              />
            </Link>

            <a
              href="#schools"
              className="text-sm underline underline-offset-4 hover:text-accent transition-colors"
            >
              Browse by school
            </a>
          </motion.div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="py-6 border-y border-border bg-secondary/40">
        <Marquee speed={40} gradient={false}>
          {[
            "Nalanda's Gurukul",
            "NEMS",
            "Namdev Bandoji Chavan",
            "Bandoji Khandoji Chavan",
          ].map((t, i) => (
            <span
              key={i}
              className="marquee-serif text-3xl md:text-4xl mx-10 text-foreground/80"
            >
              {t}

              <span className="text-accent mx-10">
                ✦
              </span>
            </span>
          ))}
        </Marquee>
      </section>

      {/* MANIFESTO */}
      <section id="about" className="py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div className="lg:sticky lg:top-28">
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-6">
                The Atelier
              </p>

              <h2 className="text-4xl md:text-5xl tracking-tight leading-tight mb-8">
                A uniform is worn{" "}
                <span className="italic text-accent">
                  200 days
                </span>{" "}
                a year. We build it like it matters.
              </h2>

              <motion.div
                initial={{
                  opacity: 0,
                  y: 30,
                }}
                whileInView={{
                  opacity: 1,
                  y: 0,
                }}
                viewport={{
                  once: true,
                }}
                transition={{
                  duration: 0.7,
                }}
                className="relative overflow-hidden"
              >
                <img
                  src={tailorImg}
                  alt="Tailoring"
                  className="w-full aspect-[4/3] object-cover"
                />
              </motion.div>
            </div>

            <div className="flex flex-col">
              {chapters.map((c, i) => (
                <motion.div
                  key={c.no}
                  initial={{
                    opacity: 0,
                    y: 30,
                  }}
                  whileInView={{
                    opacity: 1,
                    y: 0,
                  }}
                  viewport={{
                    once: true,
                    margin: "-80px",
                  }}
                  transition={{
                    duration: 0.6,
                    delay: i * 0.1,
                  }}
                  className="py-8 border-b border-border"
                  data-testid={`chapter-${c.no}`}
                >
                  <div className="flex gap-6">
                    <span className="font-mono text-accent text-sm pt-2">
                      {c.no}
                    </span>

                    <div>
                      <h3 className="text-2xl md:text-3xl mb-3">
                        {c.title}
                      </h3>

                      <p className="text-muted-foreground leading-relaxed max-w-md">
                        {c.body}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SCHOOLS */}
      <section
        id="schools"
        className="py-24 md:py-32 bg-primary text-foreground"
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-14 gap-4">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-foreground/60 mb-4">
                Institutions we dress
              </p>

              <h2 className="text-4xl md:text-5xl tracking-tight max-w-xl">
                Choose your school
              </h2>
            </div>

            <p className="text-foreground/70 max-w-sm text-sm">
              Every school has its own approved set of garments, colours and
              sizing. Select yours to view the catalog.
            </p>
          </div>

          <div className="border-t border-foreground/15">
            {/* FIX:
                Extra protection against .map() crashing */}
            {(Array.isArray(schools) ? schools : []).map(
              (s, i) => (
                <Link
                  key={s.id}
                  to={`/shop?school=${s.id}`}
                  data-testid={`school-row-${s.id}`}
                  onMouseEnter={() => setHovered(s.id)}
                  onMouseLeave={() => setHovered(null)}
                  className="group flex items-center justify-between py-8 border-b border-foreground/15 transition-colors"
                >
                  <div className="flex items-baseline gap-6">
                    <span className="font-mono text-xs text-foreground/50">
                      0{i + 1}
                    </span>

                    <span
                      className="text-3xl md:text-5xl tracking-tight transition-transform duration-500 group-hover:translate-x-3"
                      style={{
                        color:
                          hovered === s.id
                            ? s.color
                            : undefined,
                      }}
                    >
                      {s.name}
                    </span>
                  </div>

                  <span className="flex items-center gap-3 text-sm text-foreground/70 group-hover:text-foreground transition-colors">
                    <span className="hidden md:inline">
                      {s.short_name}
                    </span>

                    <ArrowUpRight
                      size={28}
                      weight="light"
                      className="group-hover:rotate-45 transition-transform duration-300"
                    />
                  </span>
                </Link>
              )
            )}
          </div>
        </div>
      </section>

      {/* FEATURE / LIFESTYLE */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid md:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{
              opacity: 0,
              scale: 0.96,
            }}
            whileInView={{
              opacity: 1,
              scale: 1,
            }}
            viewport={{
              once: true,
            }}
            transition={{
              duration: 0.8,
            }}
            className="overflow-hidden"
          >
            <img
              src={lifestyle}
              alt="Student"
              className="w-full aspect-[4/5] object-cover"
            />
          </motion.div>

          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-6">
              Bulk & institutional orders
            </p>

            <h2 className="text-4xl md:text-5xl tracking-tight leading-tight mb-6">
              Outfitting an entire school?{" "}
              <span className="italic text-accent">
                We've got the thread for it.
              </span>
            </h2>

            <p className="text-muted-foreground leading-relaxed mb-8 max-w-md">
              From size-set trials to season-wise delivery schedules, we handle
              end-to-end uniform programs for schools of every size. Talk to us
              for institutional pricing.
            </p>

            <a
              href="#contact"
              data-testid="bulk-enquiry-button"
              className="rounded-full border border-foreground px-8 py-4 text-sm uppercase tracking-widest inline-flex items-center gap-3 hover:bg-foreground hover:text-background transition-colors duration-300"
            >
              Enquire now

              <ArrowRight size={18} />
            </a>
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section
        id="contact"
        className="py-24 md:py-32 bg-secondary/50"
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-16">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-6">
              Get in touch
            </p>

            <h2 className="text-4xl md:text-5xl tracking-tight leading-tight mb-8">
              Let's tailor something dependable.
            </h2>

            <div className="space-y-4 text-lg">
              <a
                href="tel:9356070802"
                className="block hover:text-accent transition-colors"
                data-testid="contact-phone"
              >
                +91 93560 70802
              </a>

              <a
                href="mailto:info@uniform-co.com"
                className="block hover:text-accent transition-colors break-all"
                data-testid="contact-email"
              >
                info@uniform-co.com
              </a>

              <p className="text-muted-foreground">
                Dhayri, Pune, Maharashtra
              </p>
            </div>

            <a
              href="https://wa.me/919356070802"
              target="_blank"
              rel="noreferrer"
              data-testid="contact-whatsapp"
              className="mt-8 rounded-full bg-accent text-accent-foreground px-8 py-4 text-sm uppercase tracking-widest inline-flex items-center gap-3 hover:bg-foreground transition-colors duration-300"
            >
              <WhatsappLogo
                size={20}
                weight="fill"
              />

              Chat on WhatsApp
            </a>
          </div>

          <form
            onSubmit={submitEnquiry}
            className="flex flex-col gap-5"
            data-testid="enquiry-form"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <input
                value={form.name}
                onChange={(e) =>
                  setForm({
                    ...form,
                    name: e.target.value,
                  })
                }
                placeholder="Your name"
                data-testid="enquiry-name"
                className="bg-transparent border-b border-border py-3 focus:outline-none focus:border-accent transition-colors"
              />

              <input
                value={form.phone}
                onChange={(e) =>
                  setForm({
                    ...form,
                    phone: e.target.value,
                  })
                }
                placeholder="Phone"
                data-testid="enquiry-phone"
                className="bg-transparent border-b border-border py-3 focus:outline-none focus:border-accent transition-colors"
              />
            </div>

            <input
              value={form.email}
              onChange={(e) =>
                setForm({
                  ...form,
                  email: e.target.value,
                })
              }
              placeholder="Email (optional)"
              data-testid="enquiry-email"
              className="bg-transparent border-b border-border py-3 focus:outline-none focus:border-accent transition-colors"
            />

            <textarea
              value={form.message}
              onChange={(e) =>
                setForm({
                  ...form,
                  message: e.target.value,
                })
              }
              placeholder="Which school? How many sets?"
              rows={4}
              data-testid="enquiry-message"
              className="bg-transparent border-b border-border py-3 focus:outline-none focus:border-accent transition-colors resize-none"
            />

            <button
              type="submit"
              data-testid="enquiry-submit"
              className="self-start rounded-full bg-foreground text-background px-8 py-4 text-sm uppercase tracking-widest hover:bg-accent transition-colors duration-300"
            >
              Send enquiry
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
